import { INDEPENDENT_POLITICAL_PARTY_VALUES } from '@brushfire/types'

const PREFIX = 'brushfire_non_qualified_campaign'

export const NON_QUALIFIED_PROMPT_TITLE =
  "Sorry, you won't be eligible for the current campaign."
export const NON_QUALIFIED_PROMPT_BODY =
  'This campaign is only available for registered independents.'
export const NON_QUALIFIED_PROMPT_BENEFITS = [
  'You may still be eligible for future campaigns',
  'Explore other surveys on your Home screen',
  'We will notify you when new opportunities match your profile',
] as const
export const NON_QUALIFIED_PROMPT_GOT_IT = 'Got It'

function dismissKey(userId: string): string {
  return `${PREFIX}:${userId}:dismissed`
}

function readRaw(key: string): string | null {
  if (globalThis.localStorage === undefined) return null
  try {
    return globalThis.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeRaw(key: string, value: string): void {
  if (globalThis.localStorage === undefined) return
  try {
    globalThis.localStorage.setItem(key, value)
  } catch {
    /* private mode / quota */
  }
}

/** Local helper — Vite CI does not resolve `@brushfire/utils` (Node package, no dist on clone). */
export function isIndependentParty(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return (
    normalized.length > 0 &&
    INDEPENDENT_POLITICAL_PARTY_VALUES.some((option) => option.toLowerCase() === normalized)
  )
}

export function readNonQualifiedPromptDismissed(userId: string): boolean {
  return readRaw(dismissKey(userId)) === '1'
}

export function persistNonQualifiedPromptDismissed(userId: string): void {
  writeRaw(dismissKey(userId), '1')
}

export function clearNonQualifiedPromptDismissed(userId: string): void {
  if (globalThis.localStorage === undefined) return
  try {
    globalThis.localStorage.removeItem(dismissKey(userId))
  } catch {
    /* private mode */
  }
}

export type PromptProfile = {
  registeredPoliticalParty?: string | null
  ekycStatus?: string | null
  /** Present when identity returns BFP-1120; optional until @brushfire/types lands on the CI clone. */
  webSurveySignup?: boolean
}

/** True when this account was created through the SMS web survey (BFP-1120). */
export function isWebSurveySignup(profile: PromptProfile | null | undefined): boolean {
  return profile?.webSurveySignup === true
}

/**
 * Show the eligibility-exit popup when signup captured a non-Independent party
 * and KYC is not already in progress or complete.
 */
export function shouldShowNonQualifiedCampaignPrompt(
  profile: PromptProfile | null | undefined,
  userId: string | undefined,
): boolean {
  if (!userId || !profile) return false
  if (isWebSurveySignup(profile)) return false
  if (profile.ekycStatus === 'verified' || profile.ekycStatus === 'under_review') return false
  const party = profile.registeredPoliticalParty
  if (typeof party !== 'string' || party.trim() === '') return false
  return !isIndependentParty(party)
}
